import { Fragment, useEffect, useMemo, useState, type FC } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Shimmer } from '@wartek-id/shimmer'

import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { Card } from 'app/KinerjaShared/components/Card'
import { Header as HeaderBase } from 'app/KinerjaShared/components/Header'
import { PANDUAN_PENGELOLAAN_KS_URL } from 'app/KinerjaShared/utils/panduan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import EvaluationResultV2Period from 'app/KinerjaKSPage/pages/EvaluationResult/EvaluationResultV2Period'
import {
  useApiGetRiwayatByYear,
  useApiGetRiwayatTahunanByYear,
  useApiGetRiwayatHasilEvaluasiTahunan,
} from 'api/kinerja/kepala-sekolah/riwayat'
import CardStateError from 'app/KinerjaShared/components/CardStateError'
import CardStateLoading from 'app/KinerjaShared/components/CardStateLoading'
import { parseQuery } from 'utils/router'
import useToggleSdkContext from 'utils/useToogleSdk'
import EvaluationResultV2Year from 'app/KinerjaKSPage/pages/EvaluationResult/EvaluationResultV2Year'
import { PREFIX_RIWAYAT_GURU } from 'app/KinerjaKSPage/constants'

type EvaluationResultV2Props = {}

const Wrapper = ({
  className = '',
  children,
}: React.HTMLProps<HTMLDivElement>) => {
  return (
    <div
      className={clsx(
        'pb-0 pt-12 min-h-full',
        { 'print:p-0': true },
        className
      )}
    >
      <div className={'max-w-7xl mx-auto px-8'}>{children}</div>
    </div>
  )
}

const EvaluationResultV2: FC<EvaluationResultV2Props> = ({}) => {
  const router = useRouter()
  const toggleValue = useToggleSdkContext()
  const { currentNpsn } = useSchoolContext()
  const { periodYear } = usePeriodContext()

  const queryTabNo = parseQuery<string>(router?.query?.tab || '0', 'string')
  const queryTabNoInt = !!queryTabNo ? parseInt(queryTabNo, 10) : 0

  const showVersion2Tahunan =
    !!toggleValue['kinerja-ks-dokumen-evaluasi-tahunan-enabled']

  const tabs = showVersion2Tahunan ? [1, 2, 3] : [1, 2]
  const [activeTabIndex, setActiveTabIndex] = useState(
    tabs.includes(queryTabNoInt) ? queryTabNoInt - 1 : 0
  )

  const {
    isLoading: isRiwayatLoading,
    isSuccess: isRiwayatSuccess,
    isError: isRiwayatError,
    data: riwayatData,
    refetch: refetchRiwayat,
  } = useApiGetRiwayatByYear(
    {
      year: `${periodYear}`,
      roles: ['KS_PLT', 'KS_DEFINITIF'],
      npsn: currentNpsn,
    },
    { enabled: !!periodYear && !!currentNpsn }
  )

  const { isSuccess: isRiwayatTahunanSuccess, data: riwayatDataTahunan } =
    useApiGetRiwayatTahunanByYear(
      { year: `${periodYear}`, npsn: currentNpsn },
      { enabled: !!periodYear && !!currentNpsn }
    )

  const hasData =
    isRiwayatTahunanSuccess && riwayatDataTahunan?.data?.length > 0
  const riwayatIdTahunan = riwayatDataTahunan?.data?.[0]?.id
  const isGuru = riwayatDataTahunan?.data?.[0]?.role === 'GURU'
  const {
    isLoading: isHasilEvaluasiTahunanLoading,
    isSuccess: isHasilEvaluasiTahunanSuccess,
  } = useApiGetRiwayatHasilEvaluasiTahunan(riwayatIdTahunan, {
    enabled: hasData && !!riwayatIdTahunan,
  })

  const isLoading = isRiwayatLoading || isHasilEvaluasiTahunanLoading

  const riwayat = useMemo(() => {
    return riwayatData?.data?.reduce((acc, value) => {
      const key = `${value.year}_${value.period}`
      if (!acc[key]) {
        acc[key] = {}
      }
      acc[key] = value
      return acc
    }, {})
  }, [riwayatData])

  const onTabChange = (index: number) => {
    setActiveTabIndex(index)
  }

  useEffect(() => {
    if (tabs.includes(queryTabNoInt)) setActiveTabIndex(queryTabNoInt - 1)
    else setActiveTabIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTabNoInt])

  return (
    <>
      <Tabs
        defaultIndex={activeTabIndex}
        selectedIndex={activeTabIndex}
        onChange={onTabChange}
      >
        {/* Header */}
        <Card className="bg-white shadow-header">
          <Wrapper>
            <HeaderBase
              title="Hasil Penilaian Kinerja Kepala Sekolah"
              className="print:hidden"
              rightNode={
                <div className="bg-white rounded">
                  <Button
                    as="a"
                    variant="ghost"
                    rel="noopener noreferrer"
                    className="flex gap-1.5 h-14 select-none"
                    size="md"
                    href={PANDUAN_PENGELOLAAN_KS_URL}
                    target="_blank"
                    disabled={isLoading}
                  >
                    <Icon
                      fontSize="sm"
                      color={isLoading ? 'disabled' : 'informational'}
                    >
                      help
                    </Icon>
                    Baca panduan
                  </Button>
                </div>
              }
            />

            <TabList className="mt-6 bg-white">
              {tabs.map((tab, tabIndex) => {
                const key = `${periodYear}_${tab}`
                let disabled = isLoading || !riwayat?.[key]
                if (tabIndex === 2) {
                  disabled = isLoading || !isHasilEvaluasiTahunanSuccess
                }

                return (
                  <Tab
                    data-testid={`tab-${tabIndex}`}
                    key={`tab-${tabIndex}`}
                    disabled={disabled}
                  >
                    {isLoading && <Shimmer width={124} height={20} />}
                    {!isLoading && (
                      <div className="flex gap-2">
                        {tabIndex === 2 ? (
                          <div>Tahunan ({periodYear})</div>
                        ) : (
                          <>
                            <CardPeriod
                              enableMultiplePeriodSupport
                              withYear={false}
                              period={tab}
                              year={periodYear}
                            />
                          </>
                        )}
                        {disabled && <Icon color="disabled">lock</Icon>}
                      </div>
                    )}
                  </Tab>
                )
              })}
            </TabList>
          </Wrapper>
        </Card>
        <Wrapper className="!pt-11">
          {(isLoading || isRiwayatError) && (
            <>
              {isLoading && <CardStateLoading />}
              {isRiwayatError && <CardStateError onClick={refetchRiwayat} />}
            </>
          )}

          {!isLoading && isRiwayatSuccess && (
            <TabPanels>
              {tabs.map((tab, tabIndex) => {
                const key = `${periodYear}_${tab}`
                const riwayatId = riwayat?.[key]?.id

                if (tabIndex === 2) {
                  if (!isHasilEvaluasiTahunanSuccess)
                    return (
                      <TabPanel
                        data-testid={`tab-content-${tabIndex}`}
                        key={`tab-content-${tabIndex}`}
                      >
                        <CardStateError />
                      </TabPanel>
                    )

                  return (
                    <TabPanel
                      data-testid={`tab-content-${tabIndex}`}
                      key={`tab-content-${tabIndex}`}
                    >
                      <EvaluationResultV2Year
                        riwayatId={`${
                          isGuru ? PREFIX_RIWAYAT_GURU : ''
                        }${riwayatIdTahunan}`}
                        npsn={currentNpsn}
                      />
                    </TabPanel>
                  )
                }

                return (
                  <TabPanel
                    data-testid={`tab-content-${tabIndex}`}
                    key={`tab-content-${tabIndex}`}
                  >
                    <EvaluationResultV2Period riwayatId={riwayatId} />
                  </TabPanel>
                )
              })}
            </TabPanels>
          )}
        </Wrapper>
      </Tabs>
    </>
  )
}

export default EvaluationResultV2
